/**
 * Browser translation extensions (Google Translate, Edge Translator, etc.) mutate the
 * live DOM outside React's knowledge (e.g. wrapping text nodes in <font> tags). React's
 * fiber tree no longer matches the actual DOM, so a later reconciliation calls
 * removeChild/insertBefore against a node that isn't where React expects, and the
 * browser throws NotFoundError, crashing the whole app.
 *
 * This patches Node.prototype to make those two calls no-ops (instead of throwing) when
 * the DOM has already diverged from what the caller expects, since the extension already
 * moved/replaced the node and there's nothing meaningful left for React to remove/insert.
 *
 * removeChild/insertBefore only ever throw NotFoundError for this exact parent/child
 * mismatch (per the DOM spec), so once this patch is active there's no other way for
 * that error to reach Sentry from this window — anything that still throws it did not
 * go through here (e.g. it happened inside a separate iframe realm). That's why
 * interventions are reported proactively from here, instead of trying to recognize and
 * drop the exception after the fact in Sentry's beforeSend, which would risk masking an
 * unrelated real bug that happens to throw the same generic browser error.
 *
 * See: https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
 */
import * as Sentry from '@sentry/react';

const PATCHED_MARKER = '__domTranslateCrashGuardPatched';
const CONSOLE_LOG_LIMIT = 5;
const REPORT_INTERVAL_MS = 60_000;

let interventionCount = 0;
let lastReportedAt = 0;

function reportIntervention(kind: 'removeChild' | 'insertBefore') {
  interventionCount += 1;

  if (interventionCount <= CONSOLE_LOG_LIMIT) {
    // eslint-disable-next-line no-console
    console.error(
      `domTranslateCrashGuard: prevented a ${kind} crash caused by a DOM mutation outside React's control (likely a browser translation extension)`
    );
  }

  const now = Date.now();
  if (now - lastReportedAt > REPORT_INTERVAL_MS) {
    lastReportedAt = now;
    Sentry.captureMessage('domTranslateCrashGuard intervened', {
      level: 'info',
      tags: { source: 'domTranslateCrashGuard', kind },
      extra: { interventionCount },
    });
  }
}

if (
  typeof Node === 'function' &&
  Node.prototype &&
  !(Node.prototype.removeChild as unknown as Record<string, boolean>)[
    PATCHED_MARKER
  ]
) {
  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      reportIntervention('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };
  (Node.prototype.removeChild as unknown as Record<string, boolean>)[
    PATCHED_MARKER
  ] = true;

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportIntervention('insertBefore');
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
  (Node.prototype.insertBefore as unknown as Record<string, boolean>)[
    PATCHED_MARKER
  ] = true;
}
